import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { isOidcEnabled, getOidcLoginUrl } from '../../config/oidc';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Route wrapper that requires authentication
 * In OIDC mode: Redirects directly to Authentik SSO
 * In local/hybrid mode: Redirects to login page
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, authMode } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    // In pure OIDC mode, redirect directly to Authentik SSO
    if (authMode === 'oidc' && isOidcEnabled()) {
      window.location.href = getOidcLoginUrl();
      // Return loading spinner while redirecting
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    
    // In local/hybrid mode, go to login page
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};
